import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, Divider, Typography } from 'antd';

import { HotjarEvents, submitEvent } from 'hotjar';
import { getProductTypeTitle } from 'v2/helpers/productTypeTitle';
import { usePostMessage } from 'v2/hooks/usePostMessage';
import { PostMessageAction, PostMessageSource } from 'v2/interfaces/post-message';
import { EProductType } from 'v2/interfaces/product';
import { npsService } from 'v2/services/nps';
import { physicalDeliveryStore } from 'v2/store/PhysicalDelivery';

import CreateVariation from './CreateVariation';
import ProductDetails from './ProductDetails';
import SectionMore from './SectionMore';
import ShareableLinks from './ShareableLinks';
import { useWizardState } from '../../../contexts/WizardStateContext';
import { SolidaritySquadModal } from '../../SolidaritySquad/Modal';

const CREATE_PRODUCT_FEEDBACK_ID = 796843;
const CREATE_PRODUCT_EXPIRATION_IN_DAYS = 90;

function Preview() {
  const { product: wizardProduct, resetProduct, resetWizard, resetSelected } = useWizardState();
  const product = wizardProduct!;
  const sendPostMessage = usePostMessage();
  const [isSolidaritySquadModalOpen, setIsSolidaritySquadModalOpen] = useState(true);

  useEffect(() => {
    sendPostMessage({
      source: PostMessageSource.PRODUCT_MANAGER,
      action: PostMessageAction.PRODUCT_CREATED,
      data: {
        product_id: product.id,
        is_draft: false,
        completed_at: new Date().toISOString(),
        product_type: product.type,
        product_type_title: getProductTypeTitle(product.type)
      }
    });

    (async () => {
      const lastAnswer = await npsService.getSurveyLastAnswer(CREATE_PRODUCT_FEEDBACK_ID);

      if (lastAnswer) {
        const isSurveyExpired = lastAnswer.answered && lastAnswer.days_count >= CREATE_PRODUCT_EXPIRATION_IN_DAYS;
        const shouldShowSurvey = !lastAnswer.answered || isSurveyExpired;

        if (shouldShowSurvey) {
          submitEvent(HotjarEvents.PRODUCT_CREATED);
        }
      }
    })();
  }, [sendPostMessage, product.id, product.type]);

  const handleCloseSolidaritySquadModal = useCallback(() => {
    setIsSolidaritySquadModalOpen(false);
  }, []);

  const handleResetWizard = useCallback(() => {
    try {
      resetProduct();
      resetWizard(1);
      resetSelected(1);
      physicalDeliveryStore.getState().reset();

      const currentUrl = window.location.host;
      window.history.replaceState(null, null, `${currentUrl}/producer/product/newproduct`);
    } catch (error) {}
  }, [resetProduct, resetWizard, resetSelected]);

  return (
    <>
      <SolidaritySquadModal
        isOpen={isSolidaritySquadModalOpen}
        handleClose={handleCloseSolidaritySquadModal}
        productId={product.id}
      />

      <div className='mx-auto flex flex-col gap-4'>
        <div>
          <Typography.Title level={3}>Tudo certo!</Typography.Title>
          <Typography.Paragraph>
            Seu produto {product.title} está em moderação. Mas você já pode começar a vender.
          </Typography.Paragraph>
        </div>

        <ProductDetails product={product} />

        {product.type !== EProductType.PHYSICAL && (
          <ShareableLinks
            productId={product.hash ?? product.id}
            showOnlyCheckoutSunV4={product.show_only_checkout_sun_v4}
          />
        )}

        <CreateVariation product={product} />

        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
          <SectionMore product={product} />
        </div>

        <Divider />
        <div className='flex flex-wrap items-center justify-end gap-2 max-sm:[&_a]:flex max-sm:[&_button]:grow'>
          <Link id='link-go-to-products' to='/producer/product/myproducts'>
            <Button id='new-product-preview-go-products'>Ir para meus produtos</Button>
          </Link>
          <Button id='link-go-to-new-product' type='primary' onClick={handleResetWizard}>
            Cadastrar novos produtos
          </Button>
        </div>
      </div>
    </>
  );
}

export default Preview;
